import { createContext, FC, useContext, useEffect } from 'react';
import { useFragment } from 'react-relay';
import { PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { rectSortingStrategy } from '@dnd-kit/sortable';
import graphql from 'babel-plugin-relay/macro';

import Sorting, { useSorting } from 'components/Sorting';

import { MediaEditorSort_media$key } from '__generated__/MediaEditorSort_media.graphql';
import { MediaEditorSortMutation } from '__generated__/MediaEditorSortMutation.graphql';

type Props = {
  mediableId: string;
  media: MediaEditorSort_media$key | null;
  onStatusChange?: (status: boolean) => void;
};

type MediaSortContextType = {
  sorting: boolean;
};

const MediaSortContext = createContext<MediaSortContextType>({
  sorting: false,
});

export const useMediaSort = () => useContext(MediaSortContext);

const MediaEditorSort: FC<Props> = props => {
  const { mediableId, children, onStatusChange } = props;

  const media = useFragment(
    graphql`
      fragment MediaEditorSort_media on MediaConnection {
        __id
        edges {
          node {
            id
          }
        }
      }
    `,
    props.media
  );

  const connectionID = media?.__id;

  const [sort, sorting] = useSorting<MediaEditorSortMutation>(
    graphql`
      mutation MediaEditorSortMutation(
        $mediableId: ID!
        $positions: [EntityWithPositionInput!]!
      ) {
        updateMediaPositions(mediableId: $mediableId, positions: $positions) {
          id
        }
      }
    `,
    connectionID,
    positions => ({ mediableId, positions })
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    })
  );

  useEffect(() => {
    onStatusChange && onStatusChange(sorting);
  }, [onStatusChange, sorting]);

  return (
    <MediaSortContext.Provider value={{ sorting }}>
      <Sorting
        onSort={sort}
        sensors={sensors}
        edges={media?.edges ?? []}
        strategy={rectSortingStrategy}>
        {children}
      </Sorting>
    </MediaSortContext.Provider>
  );
};

export default MediaEditorSort;
